import {PolymerElement} from '@polymer/polymer';
import {ScopeBinding, ScopeRoot} from '../behaviors/keli-binding-behavior';
import {timeOut} from '@polymer/polymer/lib/utils/async.js';
import {Debouncer} from '@polymer/polymer/lib/utils/debounce.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status';

class KeliBrowseController extends ScopeRoot(ScopeBinding(PolymerElement)) {
  static get is() {
    return 'keli-browse-controller'
  }

  static get properties() {
    // @formatter:off
    return {
      backgroundExistsCache: {type: Object},
      caption: {type: String, scopevar: true, observer: 'captionChanged'},
      cacheNeedsToBeUpdated: {type: Boolean},
      colorExistsCache: {type: Object},
      currentRenderPageRange: {type: Object},
      dataLoaded: {type: Boolean, value: false},
      element: {type: Object},
      insertable: {type: Boolean, scopevar: true, observer: 'insertableChanged'},
      loadAllRows: {type: Boolean},
      loadedDataIndex: {type: Object},
      loadedDataIndexQueryHash: {type: String},
      loadRowsBatchSize: {type: Number, value: 4},
      manualKeliReady: {type: Boolean, value: true},
      multiSelect: {type: Boolean, scopevar: true, observer: 'multiSelectChanged'},
      multiSelectElementId: {type: String, value: 'browseMultiSelectColumn'},
      multiSelectElementName: {type: String, value: 'keli-browse-multiselect-column'},
      multiSelectGroupState: {type: Number, scopevar: '_multi_select_group_state', observer: 'multiSelectGroupStateChanged'},
      multiSelectTotalCount: {type: Number, scopevar: '_multi_select_total_count', observer: 'multiSelectTotalCountChanged'},
      o: {type: Array, scopevar: {options: {view: true}}, observer: 'oChanged'},
      panelAllDataLoaded: {type: Boolean, value: false},
      renderSequenceNumber: {type: Number, value: 0},
      rowsCount: {type: Number, scopevar: true, observer: 'rowsCountChanged'},
      startAtBottom: {type: Boolean, scopevar: true}
    }
    // @formatter:on
  }

  constructor() {
    super();
    this.addEmitToKeli(['browseControlCreate', 'browseControlDelete']);
    this.addOnKeli({
      'uiBrowseFilterFieldFocus': 'handleBrowseFilterFieldFocus',
      'uiBrowseRowFocus': 'handleBrowseRowFocus',
      'uiReloadData': 'handleReloadData',
      'uiBrowseObjectVisChanged': 'handleBrowseObjectVisChanged',
      'uiBrowseColumnPositionChanged': 'debouncedHandleColumnPositionChanged',
      'uiTableCellChanged': 'handleCellChanged',
      'uiTableRowChanged': 'handleRowChanged',
      'uiTableRowIndexChanged': 'handleIndexChanged',
      'uiMultiSelectUpdate': 'handleMultiSelectUpdate',
      'uiBrowseClear': 'handleBrowseClear'
    });
  }

  connectedCallback() {
    super.connectedCallback();

    this.loadedDataIndex = new Map();

    this.scrollOrResizeHandler = throttle(async event => {
      if (!this.isConnected || !this.dataLoaded) {
        return;
      }
      const pageInfo = event?.detail?.pageInfo || this.element.getPageInfo();
      await this.loadAndRenderBrowseData(event, null, pageInfo.top, pageInfo.size);
    }, 50);
  }

  async elementReady(element) {
    this.element = element;

    await this.changeKeliScopeRootName(this.element.keliname);
    await this.keliReady(this.element.getKeliScope());

    this._boundHandleElementDown = this.handleElementDown.bind(this);
    this._boundHandleElementUp = this.handleElementUp.bind(this);
    this._boundHandleBrowseScrolled = this.handleBrowseScrolled.bind(this);
    this._boundHandleBrowseResized = this.handleBrowseResized.bind(this);
    this._boundHandleSetupBrowseOrder = this.handleSetupBrowseOrder.bind(this);
    this._boundHandleKeliBrowseConfigurationMenuReset = this.handleKeliBrowseConfigurationMenuReset.bind(this);
    this._boundHandleMenuSortableReordered = this.handleMenuSortableReordered.bind(this);
    this._boundHandleMenuItemToggled = this.handleMenuItemToggled.bind(this);
    this._boundHandlePanelAllDataLoaded = this.handlePanelAllDataLoaded.bind(this);
    this._boundHandleMultiSelectChanged = this.handleMultiSelectChanged.bind(this);
    this.element.addEventListener('down', this._boundHandleElementDown);
    this.element.addEventListener('up', this._boundHandleElementUp);
    this.element.addEventListener('browseScrolled', this._boundHandleBrowseScrolled);
    this.element.addEventListener('browseResized', this._boundHandleBrowseResized);
    this.element.addEventListener('setupBrowseOrder', this._boundHandleSetupBrowseOrder);
    this.element.addEventListener('keliBrowseConfigurationMenuReset', this._boundHandleKeliBrowseConfigurationMenuReset);
    this.element.addEventListener('menuSortableReordered', this._boundHandleMenuSortableReordered);
    this.element.addEventListener('menuItemToggled', this._boundHandleMenuItemToggled);
    this.element.addEventListener('panelAllDataLoaded', this._boundHandlePanelAllDataLoaded);
    this.element.addEventListener('keliMultiSelectChanged', this._boundHandleMultiSelectChanged);

    this.executeOnKeliReadyComplete(async () => {
      await this.runKeliFunction('uiSet', 'mode', 'browse');
      await this.initialBrowseSetup();
    });

    if (this.o) {
      this.element.setSortOrder(this.o);
    }
    if (typeof this.rowsCount === 'number') {
      this.element.rows = this.rowsCount;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.element) {
      this.element.removeEventListener('down', this._boundHandleElementDown);
      this.element.removeEventListener('up', this._boundHandleElementUp);
      this.element.removeEventListener('browseScrolled', this._boundHandleBrowseScrolled);
      this.element.removeEventListener('browseResized', this._boundHandleBrowseResized);
      this.element.removeEventListener('setupBrowseOrder', this._boundHandleSetupBrowseOrder);
      this.element.removeEventListener('keliBrowseConfigurationMenuReset', this._boundHandleKeliBrowseConfigurationMenuReset);
      this.element.removeEventListener('menuSortableReordered', this._boundHandleMenuSortableReordered);
      this.element.removeEventListener('menuItemToggled', this._boundHandleMenuItemToggled);
      this.element.removeEventListener('panelAllDataLoaded', this._boundHandlePanelAllDataLoaded);
      this.element.removeEventListener('keliMultiSelectChanged', this._boundHandleMultiSelectChanged);
    }

    this.element = null;
    this.loadedDataIndex = null;
    this.getColumns = null;
  }

  async scrollHandler(event) {
    await this.scrollOrResizeHandler(event);
  }

  async debouncedScrollHandler(event) {
    return new Promise(resolve => {
      this._handleDebouncedScrollHandlerJob = Debouncer.debounce(this._handleDebouncedScrollHandlerJob, timeOut.after(200), async () => {
        await this.scrollOrResizeHandler(event);
        resolve();
      });
    });
  }

  handleElementDown() {
    this.isPointerDown = true;
  }

  handleElementUp() {
    this.isPointerDown = false;
  }

  async handleBrowseScrolled(event) {
    if (this.isPointerDown) {
      await this.debouncedScrollHandler(event);
    } else {
      await this.scrollHandler(event);
    }
  }

  async handleBrowseResized(event) {
    await this.scrollOrResizeHandler(event);
  }

  handleSetupBrowseOrder(event) {
    ++this.renderSequenceNumber;
    this.o = event.detail;
  }

  async handleKeliBrowseConfigurationMenuReset() {
    await this.runKeliFunction('clearCurrentView');
  }

  handleMenuSortableReordered(event) {
    const items = event.detail.element.columns;
    if (items) {
      const templateInstance = this.getKeliTemplateInstance();
      items.forEach((item, index) => {
        this.runKeliFunctionIn(templateInstance.forkThread(), 'uiSet', `${item.keliname}.position`, (index + 1), {
          p: true,
          confine: true,
          view: true
        });
      });
    }
  }

  async handleMenuItemToggled(event) {
    await this.runKeliFunction('uiSet', `${event.detail.keliname}.vis`, event.detail.toggle, {
      p: true,
      confine: true,
      view: true
    });
  }

  async handlePanelAllDataLoaded() {
    this.panelAllDataLoaded = true;
    await this.setupMenu();
  }

  handleMultiSelectChanged(event) {
    if (event.detail.id) {
      this.handleCellChanged({
        rowid: event.detail.id,
        field: this.multiSelectElementName,
        property: 'value',
        value: event.detail.state,
        loadedDataIndexOnly: true
      });
    } else if (event.detail.state !== undefined) {
      this.multiSelectGroupState = (event.detail.state ? 1 : 0);
    }
  }

  async handleBrowseFilterFieldFocus(event) {
    if (!this.element) {
      return;
    }
    this.element.moveFilterFieldFocus(event.name);
  }

  async handleBrowseRowFocus(event) {
    if (!this.element) {
      return;
    }
    if (event.skip) {
      this.element.movePosition(event.skip);
    } else if (event.index) {
      this.element.movePosition(null, null, event.index);
    }
  }

  clearLoadedDataIndex(queryHash) {
    this.loadedDataIndex = new Map();
    this.loadedDataIndexQueryHash = queryHash || Math.random(); // Use random to prevent "null" from being an equal value
    this.colorExistsCache = {};
    this.backgroundExistsCache = {};
  }

  async handleReloadData(event, templateInstance) {
    const clearCache = (!!event?.force || this.element.currentlyFiltering || this.element.currentlySorting);
    if (clearCache) {
      this.clearLoadedDataIndex();
    }
    await this.loadRelevantData(event, templateInstance, {updateCache: true, clearCache, isReload: true});
  }

  async handleBrowseObjectVisChanged(event, templateInstance) {
    let objectName = event.objectname;
    if (Array.isArray(objectName)) {
      if (objectName.length > 1) {
        objectName = objectName.slice(0, -1).join('.');
      } else {
        objectName = objectName.join('');
      }
    }
    if (await this.runKeliFunctionIn(templateInstance, 'get', `${objectName}.controltype`, {
      p: true,
      peek: true
    }) === 'menuitem') {
      this.dispatchEvent(new CustomEvent('keliControlObjectVisChanged', {
        bubbles: true, composed: true,
        detail: {
          id: `${this.element.keliname}_${objectName}`,
          keliname: objectName,
          event,
          controller: this
        }
      }));
      return;
    }

    await this.resetColumns(event, templateInstance);
  }

  async debouncedHandleColumnPositionChanged(event, templateInstance) {
    return new Promise(resolve => {
      this._handleDebouncedHandleColumnPositionChangedJob = Debouncer.debounce(this._handleDebouncedHandleColumnPositionChangedJob, timeOut.after(100), async () => {
        await this.resetColumns(event, templateInstance);
        resolve();
      });
    });
  }

  async handleCellChanged(event) {
    let property = event.property;
    let value = event.value;
    switch (event.property) {
      case 'displayvalue':
        property = 'value';
        break;
      case 'backgroundcolor':
        property = 'background';
        break;
    }
    if (!property) {
      return;
    }

    // Update cached data.
    const cachedRow = this.loadedDataIndex?.get(event.rowid);
    if (cachedRow?.cells?.[event.field]) {
      cachedRow.cells[event.field][property] = value;
    }

    if (event.loadedDataIndexOnly) {
      return;
    }

    const row = cachedRow?.rendered;
    if (!row) {
      return;
    }

    const cell = row.querySelector(`[data-keliname="${event.field}"]`);
    if (!cell) {
      return;
    }

    if (property === 'value') {
      this.formatCellContents(cell, value);
    } else if (property === 'background') {
      row.cellColor(cell, value, cell.dataset.color);
    } else if (property === 'color') {
      row.cellColor(cell, cell.dataset.background, value);
    }

    // Update the EIP row if it's visible.
    if (this.element?.$?.editRow?.row && this.element.$.editRow.editing && this.element.$.editRow.row.rowId === event.rowid) {
      this.element.$.editRow.setContentsFromRowCell(event.rowid, cell, null, value, false);
    }
  }

  async handleRowChanged(event) {
    let property = event.property;
    let value = event.value;
    if (event.property === 'backgroundcolor') {
      property = 'background';
    }
    if (!property) {
      return;
    }

    // Update cached data.
    const cachedRow = this.loadedDataIndex?.get(event.rowid);
    if (cachedRow) {
      cachedRow[property] = value;
    }

    const row = cachedRow?.rendered;
    if (!row) {
      return;
    }

    if (property === 'background') {
      row.color(value, row.dataset.color);
    } else if (property === 'color') {
      row.color(row.dataset.background, value);
    }
  }

  async handleIndexChanged(event) {
    const newIndex = event.idx + 1;
    const cachedRow = this.loadedDataIndex?.get(event.rowid);
    if (cachedRow) {
      cachedRow.position = newIndex;
      if (!event.indexOnly && cachedRow.rendered) {
        cachedRow.rendered.position = newIndex;
      }
    }

    if (!event.indexOnly && cachedRow) {
      let row = cachedRow.rendered;
      if (row?.selected) {
        this.element.selectedRowNumber = row.position;
      }
    }
  }

  async handleMultiSelectUpdate(event) {
    let rowIds = event.id;
    if (!Array.isArray(rowIds)) {
      rowIds = [rowIds];
    }
    rowIds.forEach((id) => {
      let cachedRow = this.loadedDataIndex.get(id);
      if (cachedRow && cachedRow.cells && cachedRow.cells[this.multiSelectElementName]) {
        cachedRow.cells[this.multiSelectElementName].value = event.state;
      }

      let row = cachedRow?.rendered;
      if (row) {
        let columns = this.getColumns();
        for (let i = 0; i < columns.length; ++i) {
          if (columns[i].name === this.multiSelectElementName) {
            this.configureMultiSelectToggleElement(row.children[i], id, event.state);

            // Update the EIP row if it's visible.
            if (this.element?.$?.editRow?.row && this.element.$.editRow.editing && this.element.$.editRow.row.rowId === id) {
              this.element.$.editRow.setContentsFromRowCell(id, row.children[i], null, event.state, false);
            }
            break;
          }
        }
      }
    }, this);
  }

  captionChanged(newValue) {
    if (this.element) {
      this.element.caption = newValue;
    }
  }

  insertableChanged(newValue) {
    if (this.element) {
      this.element.$.blankSlate.insertEnabled = (newValue !== false);

      if (this.element.$.blankSlate.insertEnabled && !this.element.$.blankSlate.keliTargetScopeRoot) {
        this.element.$.blankSlate.keliTargetScopeRoot = this;
        const blankSlateButton = this.element.$.blankSlate.renderRoot.querySelector('#insertButton');
        if (blankSlateButton?.getKeliScopeRoot) {
          blankSlateButton.getKeliScopeRoot().moveToKeliScopeRoot(blankSlateButton, this);
        }
      }
    }
  }

  async multiSelectChanged(event, templateInstance) {
    if (this.getColumns) {
      await this.resetColumns(event, templateInstance);
    }
  }

  multiSelectGroupStateChanged(newValue) {
    if (this.element.$.header) {
      let headerCell = this.element.$.header.querySelector(`#${this.multiSelectElementId}`);
      let state = (newValue === 1);
      let indeterminate = (newValue === 2);

      this.configureMultiSelectToggleElement(headerCell, undefined, state, indeterminate, 'multiselect');
    }
  }

  multiSelectTotalCountChanged(newValue) {
    if (this.element.$.footer) {
      const footerCell = this.element.$.footer.querySelector(`#${this.multiSelectElementId}`);
      if (footerCell) {
        footerCell.aggregateValue = newValue || 0;
      }
    }
  }

  oChanged(newValue) {
    if (this.element) {
      this.element.setSortOrder(newValue);
    }
  }

  handleBrowseClear() {
    this.removeOutOfBoundsRows();
    this.clearLoadedDataIndex();
    if (this.element) {
      this.element.selectedRowNumber = 0;
      this.element.selectedRow = null;
    }
  }

  rowsCountChanged(newValue) {
    if (!this.isConnected || !this.element) return;
    this.element.rows = newValue;

    // Check if the browse is user filtered.
    if (this.element.$.blankSlate) {
      this.runKeliFunction('isBrowseUserFiltered').then(filtered => {
        this.element.$.blankSlate.filtered = filtered;
      });
    }
  }

  getHeightOfBrowse() {
    let multiLineHeight = 0;
    let cachedRows = 0;
    if (this.element?.multiLine) {
      for (const row of this.loadedDataIndex.values()) {
        multiLineHeight += row.height || this.element.rowHeight;
        ++cachedRows;
      }
    }
    return multiLineHeight + (((this.rowsCount || 0) - cachedRows) * this.element.rowHeight);
  }

  updateAllRowHeights() {
    if (!this.isConnected) return;
    keliUITimingEvents && keliUITimer('UI_Browse_updateAllRowHeights');
    const existingRows = Array.from(this.element.$.body.querySelectorAll('keli-browse-row'));

    let startingBrowseHeight;
    if (this.element.multiLine) {
      startingBrowseHeight = parseInt(this.element.$.body.style[(this.element.multiLine ? 'minHeight' : 'height')], 10);
      for (const rowElement of existingRows) {
        const cachedRow = this.loadedDataIndex?.get(rowElement.rowId);
        if (cachedRow) {
          cachedRow.height = rowElement.clientHeight;
        }
      }
    }

    for (const rowElement of existingRows) {
      rowElement.updateRowPositionOffset(this.getPositionOffsetForRow(rowElement.position));
    }

    if (this.element.multiLine) {
      this.element.updateBrowseHeight(startingBrowseHeight);
    }

    keliUITimingEvents && keliUITimer('UI_Browse_updateAllRowHeights', true);
  }

  getPositionOffsetForRow(rowPosition) {
    if (!this.isConnected) return;
    keliUITimingEvents && keliUITimer(`UI_Browse_getPositionOffsetForRow_${rowPosition}`);
    let priorHeights = 0;
    let cachedRows = 0;
    if (this.element?.multiLine) {
      for (const row of this.loadedDataIndex.values()) {
        if (row.position < rowPosition) {
          priorHeights += row.height || this.element.rowHeight;
          ++cachedRows;
        }
      }
    }
    keliUITimingEvents && keliUITimer(`UI_Browse_getPositionOffsetForRow_${rowPosition}`, true);
    return priorHeights + (((rowPosition || 0) - 1 - cachedRows) * this.element.rowHeight);
  }

  getViewportTop(scrollTop) {
    if (!this.isConnected) return;
    keliUITimingEvents && keliUITimer(`UI_Browse_getViewportTop_${scrollTop}`);
    const minRows = Math.min(this.rowsCount || Number.MAX_SAFE_INTEGER, Math.floor(scrollTop / this.element.rowHeight));
    let top = 0;
    let foundHeight = 0;
    if (this.loadedDataIndex) {
      const indexByPosition = new Map();
      for (const row of this.loadedDataIndex.values()) {
        indexByPosition.set(row.position, row);
      }
      for (let i = 1; i < minRows; ++i) {
        foundHeight += indexByPosition.get(i)?.height || this.element.rowHeight;
        if (foundHeight < scrollTop) {
          ++top;
        }
      }
    }
    keliUITimingEvents && keliUITimer(`UI_Browse_getViewportTop_${scrollTop}`, true);
    return Math.min(minRows, top);
  }

  updateLastSelectedRow(rowId) {
    if (!this.isConnected) return;
    let override = {scopevar: 'row', value: rowId, scope: this.getKeliScopeId()};
    this.setScopevarOverride(override.scope, override.scopevar, override.value);
    this.dispatchEvent(new CustomEvent('keliOverrideScopeVar', {bubbles: true, composed: true, detail: override}));
  }

  async rowExistsInDataset(rowId) {
    const rowExists = await this.runKeliFunction('rowsExists', rowId);
    if (rowExists) {
      return true;
    }
    const rowToSelect = await this.runKeliFunction('getRowToSelectDetails', rowId);
    if (rowToSelect) {
      return true;
    }
    return false;
  }

  async initialBrowseSetup() {
    const templateInstance = this.getKeliTemplateInstance();
    await this.setupColumns(templateInstance);
    if (!this.isConnected) return;

    afterNextRender(this.element.$.browseBodyScroll, async () => {
      const event = {};
      const rowToSelect = await this.runKeliFunctionIn(templateInstance, 'get', 'rowToSelect', {p: true});
      if (!this.isConnected) return;
      if (rowToSelect) {
        event.rowtoselect = rowToSelect;
      }

      await this.loadRelevantData(event, templateInstance, {initialLoad: true});
    });
  }

  async setupMenu() {
    keliUITimingEvents && keliUITimer('UI_Browse_setupMenu');
    const browseMenus = await this.runKeliFunction('getControlMenuDefinitions');
    const menuItems = [];
    for (let i = 0; i < browseMenus.list.length; ++i) {
      menuItems.push({
        id: browseMenus.list[i].id,
        name: browseMenus.list[i].name,
        parentCaption: browseMenus.list[i].parentcaption,
        region: browseMenus.list[i].region,
        hotkey: browseMenus.list[i].hotkey,
        dependsOnBrowse: browseMenus.list[i].dependsonbrowse,
        keliTargetScopeRoot: this,
        registerToElement: this.element
      });
    }
    this.dispatchEvent(new CustomEvent('keliControlMenuReady', {
      bubbles: true, composed: true,
      detail: {list: menuItems}
    }));
    keliUITimingEvents && keliUITimer('UI_Browse_setupMenu', true);
  }

  async setupColumns(templateInstance) {
    if (!this.isConnected) return;
    templateInstance = this.getKeliTemplateInstance(templateInstance);

    let columns = await this.runKeliFunctionIn(templateInstance, 'getBrowseColumns', {checkvis: false});
    if (!this.isConnected) return;

    if (!Array.isArray(columns)) {
      columns = [];
    } else {
      columns = Array.from(columns);
    }

    const visibleColumns = [];

    if (this.multiSelect) {
      columns.unshift({
        name: this.multiSelectElementName,
        visible: true,
        caption: '',
        _multiSelectColumn: true
      });
    }

    const existing = this.getColumns && this.getColumns();
    if (existing) {
      for (let i = existing.length - 1; i >= 0; --i) {
        if (existing[i] && existing[i].element) {
          existing[i].element.remove();
          existing[i].footerElement.remove();
          existing[i].eipElement && existing[i].eipElement.remove();
          existing.splice(i, 1);
        }
      }
    }

    const buildConfigurationArray = (this.element.$.modifyColumnsMenu?.columns.length === 0);
    for (let i = 0; i < columns.length; ++i) {
      if (!this.disableBrowseConfiguration) {
        if (buildConfigurationArray && !columns[i]._multiSelectColumn && columns[i].allowcontrolconfiguration !== false) {
          this.element.$.modifyColumnsMenu.columns.push({
            keliname: columns[i].name,
            checked: columns[i].visible,
            caption: columns[i].caption
          });
          this.element.$.modifyColumnsMenu.requestUpdate();
        }
      }

      if (columns[i].visible) {
        const headerCell = document.createElement('keli-browse-header-cell');
        headerCell.keliname = columns[i].name;
        headerCell.keliTargetScopeRoot = this;
        headerCell.browse = this.element;
        if (columns[i]._multiSelectColumn) {
          headerCell.id = this.multiSelectElementId;
          headerCell.skip = true;
          headerCell.filterable = false;
          headerCell.sortable = false;
          headerCell.resizable = false;
          headerCell.justify = 'center';
          headerCell.width = 38;
          headerCell.slottedContentOnly = true;
          headerCell.justifyCell = 'center';
          this.configureMultiSelectToggleElement(headerCell, undefined, false, undefined, 'multiselect');
        }
        this.element.$.header.appendChild(headerCell);

        const footerCell = document.createElement('keli-browse-footer-cell');
        footerCell.keliname = columns[i].name;
        footerCell.keliTargetScopeRoot = this;
        if (columns[i]._multiSelectColumn) {
          footerCell.id = this.multiSelectElementId;
          footerCell.width = 38;
          footerCell.onActivateEventName = 'keliMultiSelectFilterToAll';
        }
        this.element.$.footer.appendChild(footerCell);

        const componentMeta = KeliInterface.registeredKeliControlTypes[columns[i].controltype];
        visibleColumns.push({
          name: columns[i].name,
          element: headerCell,
          footerElement: footerCell,
          elementType: componentMeta && componentMeta.tagName,
          eip: columns[i].eip,
          db: columns[i].db,
          _multiSelectColumn: columns[i]._multiSelectColumn
        });
      }
    }

    this.getColumns = () => visibleColumns;
  }

  async resetColumns(event, templateInstance) {
    if (!event) {
      return;
    }
    templateInstance = this.getKeliTemplateInstance(templateInstance);

    return new Promise(resolve => {
      this._handleResetColumnsJob = Debouncer.debounce(this._handleResetColumnsJob, timeOut.after(25), async () => {
        await this.runKeliFunctionIn(templateInstance, 'clearAllBrowseColumns');
        this.removeOutOfBoundsRows();
        this.clearLoadedDataIndex();
        await this.setupColumns(templateInstance);
        await this.refreshPage(event, templateInstance, null, {clearCache: true});
        this.clearEditableBrowseCells(this.element.$.editRow);
        resolve();
      });
    });
  }

  clearEditableBrowseCells(element) {
    if (element.browse) {
      element.browse.editing = false;
    }
    if (element.keliname) {
      element.keliname = null;
    }
    const cells = element.querySelectorAll('span.cell');
    for (const cell of cells) {
      cell.remove();
    }
  }

  createEditableBrowseCells(element, objectKeyName) {
    const columns = this.getColumns && this.getColumns() || [];
    for (const column of columns) {
      const cell = document.createElement('span');
      cell.dataset.keliname = column.name;
      cell.classList.add('cell');
      element.handleBrowseCellAttributes({
        detail: {
          cell,
          width: column.element.width,
          justify: column.element.justify
        }
      });
      if (column._multiSelectColumn) {
        cell.dataset.multiselect = 'multiselect';
      } else if (!column.elementType || column.db === false || column.eip === false) {
        cell.classList.add('empty');
      } else if (['keli-markdown', 'keli-textarea', 'keli-lookup', 'keli-value', 'keli-codearea'].includes(column.elementType)) {
        cell.classList.add('empty');
      } else {
        const field = document.createElement(column.elementType);
        field.keliTargetScopeRoot = this;
        field.keliname = column.name;
        field.unbindScopevars = ['caption', 'focus', 'value', 'displayvalue'];
        field.unbindEmitToKeliEvents = ['keliuserchange', 'kelifocus', 'keliblur'];
        field.preventPanelEvents = true;
        field.sendEditRowEvents = true;
        field.forcePropagation = true;
        field.chrome = false;
        cell.appendChild(field);
      }
      element.appendChild(cell);
      if (objectKeyName) {
        column[objectKeyName] = cell;
      }
    }
  }

  async refreshPage(event, templateInstance, pageInfo, options) {
    pageInfo = pageInfo || this.element.getPageInfo();
    await this.loadAndRenderBrowseData(event, templateInstance, pageInfo.top, pageInfo.size, options);
  }

  async getDefaultPosition(templateInstance, trustRowsCount = true) {
    templateInstance = this.getKeliTemplateInstance(templateInstance);
    if (this.loadAllRows || this.startAtBottom) {
      if (!trustRowsCount || !Number.isInteger(this.rowsCount)) {
        this.rowsCount = (await this.runKeliFunctionIn(templateInstance, 'rowsGetLength')) || 0;
      }
    }

    let selectPosition;
    if (this.startAtBottom) {
      selectPosition = this.rowsCount || 1;
    } else {
      selectPosition = 1;
    }
    return selectPosition;
  }

  async loadRelevantData(event, templateInstance, {
    initialLoad = false,
    clearCache = false,
    updateCache = false,
    isReload = false
  } = {}) {
    templateInstance = this.getKeliTemplateInstance(templateInstance);
    if (!this.isConnected) return;

    if (updateCache) {
      this.cacheNeedsToBeUpdated = true;
    }

    const resetToDefaultPosition = !!(initialLoad || this.element.currentlyFiltering || this.element.currentlySorting);
    const rowToSelect = (!this.element.currentlyFiltering && !this.element.currentlySorting && event?.rowtoselect);
    const startingRowId = this.element.selectedRow?.rowId;

    let selectPosition;
    let selectRowId = rowToSelect || startingRowId;
    try {
      const controller = this;
      await this.runKeliFunctionIn(templateInstance, 'supercede', {
        domain: `selectPosition_${controller.element.keliname}`,
        func: async function () {
          // Determine the position of the row we want to focus on after the reload.
          if (rowToSelect) {
            const rowToSelectDetails = await this.getRowToSelectDetails(rowToSelect);
            if (rowToSelectDetails) {
              selectRowId = rowToSelectDetails.rowtoselect;
              selectPosition = rowToSelectDetails.rowtoselectposition;
            }
          } else if (resetToDefaultPosition) {
            // Wait for parameters required to get the default position to prevent race conditions.
            await controller.waitForScopeVarInitialization('loadAllRows');
            await controller.waitForScopeVarInitialization('startAtBottom');

            selectPosition = await controller.getDefaultPosition(this, false);
          }
        }
      });
    } catch (error) {
      if (!error.superceded) {
        throw error;
      }
      return;
    }
    if (!this.isConnected) return;

    const startingRowTop = this.getPositionOffsetForRow(this.element.selectedRowNumber);
    const startingRowBottom = startingRowTop + (this.loadedDataIndex.get(startingRowId)?.height || this.element.rowHeight);
    const browseTop = this.element.$.browseBodyScroll.scrollTop;
    const browseBottom = (browseTop + this.element.$.browseBodyScroll.clientHeight);
    const startingRowIsOutOfView = (startingRowId && (startingRowTop > browseBottom || startingRowBottom < browseTop));

    const pageInfo = this.element.getPageInfo();
    if (!this.loadAllRows && selectPosition && !startingRowIsOutOfView) {
      if (selectPosition < pageInfo.top) {
        pageInfo.top = selectPosition;
      } else if (selectPosition > pageInfo.top + pageInfo.size) {
        pageInfo.top = Math.max(selectPosition - Math.max(pageInfo.size, 1), 0);
      }
    }

    await this.refreshPage(event, templateInstance, pageInfo, {initialLoad, clearCache, isReload});
    if (!this.isConnected) return;

    if (!resetToDefaultPosition) {
      // If the user has moved their row selection while reloading was happening, keep the new selection.
      if (startingRowId !== this.element.selectedRow?.rowId) {
        selectRowId = this.element.selectedRow?.rowId;
      }

      // If the row does not exist in the dataset, leave it as the current selection.
      if (selectRowId && !this.loadedDataIndex.has(selectRowId)) {
        selectRowId = this.element.selectedRow?.rowId;
      }

      if (selectRowId) {
        const matchingRowPosition = this.loadedDataIndex.get(selectRowId)?.position;
        if (matchingRowPosition) {
          selectPosition = matchingRowPosition;
        }
      }
    }

    if (selectPosition) {
      this.element.selectedRowNumber = selectPosition;
      if (initialLoad || !startingRowIsOutOfView) {
        afterNextRender(this.element.$.body, async () => {
          this.element.scrollToActiveSelection();
          await this.element.updateSelection();
        });
      }
    }
  }

  async loadAndRenderBrowseData(event, templateInstance, pageTop, pageSize, {
    initialLoad = false,
    clearCache = false,
    isReload = false
  } = {}) {
    if (!this.isConnected) return;
    templateInstance = this.getKeliTemplateInstance(templateInstance);

    const fireBrowseLoadedEvents = (visualOnly, reloaded) => {
      if (!this.isConnected) return;
      if (!this.dataLoaded && !visualOnly) {
        this.dataLoaded = true;
        this.dispatchEvent(new CustomEvent('dataLoaded', {
          bubbles: true, composed: true,
          detail: {element: this.element}
        }));
      }
      this.element.dispatchEvent(new CustomEvent('browsePageLoaded', {detail: {visualOnly, reloaded}}));
    };

    const cancelLoading = (renderSequence) => {
      if (!this.isConnected) return true;
      if (renderSequence && this.renderSequenceNumber !== renderSequence) return true;
    };

    try {
      keliUITimingEvents && keliUITimer('UI_Browse_loadAndRenderBrowseData');
      const renderSequence = ++this.renderSequenceNumber;

      const columns = this.getColumns && this.getColumns();
      if (!columns) {
        return;
      }

      const renderBreakpoint = Math.round(Math.min(pageSize / 2, 10)) || 10; // Render in pageSize chunks
      let renderStart = Math.floor(Math.max(pageTop, 0) / renderBreakpoint) * renderBreakpoint;
      let renderTotalSize = Math.ceil(((pageSize + renderBreakpoint) / renderBreakpoint) * renderBreakpoint);
      let renderEnd = (renderStart + renderTotalSize);

      // If we aren't doing a full refresh, trust the rowsCount to be correct.
      if (this.rowsCount !== undefined && (initialLoad || (!clearCache && !isReload))) {
        renderEnd = Math.min(renderStart + Math.min(renderTotalSize, this.rowsCount), this.rowsCount);
        renderTotalSize = Math.max(renderEnd - renderStart, 0);
      }
      if (this.loadAllRows) {
        renderStart = 0;
        renderEnd = Number.MAX_SAFE_INTEGER;
      } else if (!renderTotalSize) {
        fireBrowseLoadedEvents();
        this.removeOutOfBoundsRows();
        return;
      }

      let firstTimeLoadPageSize;
      if (!this.dataLoaded) {
        firstTimeLoadPageSize = pageSize;
      }
      if (clearCache) {
        this.clearLoadedDataIndex();
      }
      this.element.loadedPageInfo = {size: pageSize, top: pageTop};
      this.currentRenderPageRange = {renderStart, renderEnd};
      const {
        finalRenderEnd,
        rowIds,
        error
      } = await this.loadRangeOfData(event, templateInstance, renderStart, renderEnd, {
        initialLoad: initialLoad,
        checkCacheFirst: (!clearCache && !this.cacheNeedsToBeUpdated),
        clearRows: clearCache,
        renderSequence
      }) || {};
      if (cancelLoading(renderSequence)) return;

      // Handle loading errors by rendering the blank slate with an error message.
      if (error) {
        this.element.$.blankSlate.error = error;
        this.removeOutOfBoundsRows();
        return;
      } else {
        this.element.$.blankSlate.error = undefined;
      }

      // Cancel rendering if the loading of data was cancelled.
      if (rowIds === undefined && finalRenderEnd === undefined) {
        return;
      }
      this.cacheNeedsToBeUpdated = false;

      fireBrowseLoadedEvents();
      if (cancelLoading(renderSequence)) return;

      if (firstTimeLoadPageSize && firstTimeLoadPageSize < this.element.getPageInfo().size) {
        this.element.dispatchEvent(new CustomEvent('browseResized'));
      }
      if (this.loadAllRows && Number.isInteger(finalRenderEnd)) {
        this.currentRenderPageRange.renderEnd = finalRenderEnd;
      }
      if (clearCache) {
        this.removeOutOfBoundsRows({cached: false});
      }
      this.removeOutOfBoundsRows({notRowIds: rowIds, rendered: false});
      this.renderViewablePage(this.currentRenderPageRange.renderStart, this.currentRenderPageRange.renderEnd);

      fireBrowseLoadedEvents(true, initialLoad || clearCache);

      afterNextRender(this.element.$.body, () => {
        if (cancelLoading(renderSequence)) return;
        this.removeOutOfBoundsRows({notRowIds: rowIds, notInCache: true});
      });
    } finally {
      keliUITimingEvents && keliUITimer('UI_Browse_loadAndRenderBrowseData', true);
    }
  }

  async loadRangeOfData(event, templateInstance, pageStart, pageEnd, {
    initialLoad = false,
    checkCacheFirst = true,
    clearRows = false,
    renderSequence
  } = {}) {
    let needToPopLocalVariable = false;

    const cancelLoading = (runningHash) => {
      if (!this.isConnected) return true;
      if (runningHash && this.loadedDataIndexQueryHash !== runningHash) return true;
      if (renderSequence && this.renderSequenceNumber !== renderSequence) return true;
    };

    if (cancelLoading()) return;
    try {
      keliUITimingEvents && keliUITimer('UI_Browse_loadRangeOfData');
      const columns = this.getColumns?.();

      if (!this.loadedDataIndex) {
        this.loadedDataIndex = new Map();
      }
      if (checkCacheFirst) {
        const foundPositions = [];
        const foundRowIds = [];
        for (const row of this.loadedDataIndex.values()) {
          if (!row.cells) {
            continue;
          }
          if (row.position >= pageStart && row.position <= pageEnd) {
            foundPositions.push(row.position);
            foundRowIds.push(row.rowId);
          }
        }
        if (foundPositions.length) {
          let canUseCache = true;
          for (let i = Math.max(pageStart, 1); i <= pageEnd; ++i) {
            if (!foundPositions.includes(i)) {
              canUseCache = false;
              break;
            }
          }
          if (canUseCache) {
            return {
              finalRenderEnd: pageEnd,
              rowIds: foundRowIds
            };
          }
        }
      }

      /**
       * loadPage needs to handle two scenarios (amongst others):
       * 1. Our query has changed, so if any loadPage is currently in progress for the old query then it should be
       *    aborted and any results thrown away. This usually happens as someone is slowly typing in a filter value
       *    on a slow browse.
       * 2. Our query has not changed and we are getting rapid fire simultaneous loadPages. This happens when the browse
       *    is scrolled or resized. It _is_ throttled (currently at 50ms), but we still don't want 20 queries a second
       *    hitting the server. So in this case we consolidate those simultaneous requests by letting the initial request
       *    finish and just using its results (at page boundaries anyway).
       * To accomplish these two things we use a combination of two mechanisms:
       * 1. supercede - we supercede based on the query hash, which satisfies the first scenario.
       * 2. consolidateOperation - we build the key for this off of the current page offset and limit (the data page,
       *    not the UI page).
       **/

        // This is to avoid a race condition where `_query` can be changed in the middle of our refresh.
        // We need it to stay consistent so that the query hash done by the consolidate in `rowsGetPage` stays consistent.
      const query = await this.runKeliFunctionIn(templateInstance, 'getQueryClone', initialLoad ? {operation: 'open'} : undefined);
      if (cancelLoading()) return;
      await this.runKeliFunctionIn(templateInstance, 'pushLocalVariableOverride', '_query', query);
      if (cancelLoading()) return;
      needToPopLocalVariable = true;

      const queryHash = (await this.runKeliFunctionIn(templateInstance, 'getQueryHash', query))?.toString();
      if (this.loadedDataIndexQueryHash !== queryHash) {
        this.clearLoadedDataIndex(queryHash);
      }
      if (cancelLoading(queryHash)) return;

      // It's difficult to do this supercede at the framework level because we don't know what the page offsets will be until
      // we get into this function. Doing it only in rowsGetPage is too granular.
      // The `func` option, is a function that is scoped to a Keli binding instance. So the `this` inside
      // of the function will NOT be the controller element.
      const controller = this;
      return await this.runKeliFunctionIn(templateInstance, 'supercede', {
        domain: controller.element.keliname,
        func: async function () {
          let rowsObject;
          let rowsPage;

          // rowsGetPage is index based, so we have to adjust our numbers for a zero-based index.
          const pageStartIndex = Math.max(pageStart - 1, 0);
          let pageEndIndex = pageEnd - 1;
          const pageLimit = pageEndIndex - pageStartIndex + 1; // Rows.getRows() uses >= for begin, and < for end.

          try {
            keliUITimingEvents && keliUITimer(`UI_Browse_loadRangeOfData_rowsgetpage_${queryHash}`);
            if (clearRows) {
              const isDatabaseTemplate = await this.isdb();
              await this.checkSuperceded();
              if (isDatabaseTemplate) {
                await this.rowsClear();
                await this.checkSuperceded();
              }
            }

            rowsObject = await this.get('rows');
            await this.checkSuperceded();
            if (cancelLoading(queryHash)) return;
            if (!rowsObject) {
              return;
            }
            rowsPage = await this.rowsGetPage(rowsObject, pageStartIndex, (controller.loadAllRows ? 0 : pageLimit), Object.assign({
              idsonly: true,
              loadentirepage: true,
              consolidate: true,
              operation: (initialLoad ? 'open' : undefined),
              reload: false,
              loadallrows: (controller.element && controller.loadAllRows) || false,
              pagesize: Math.max(pageLimit, 10)
            }, {stats: event?.stats}));
            await this.checkSuperceded();
            if (cancelLoading(queryHash)) return;
          } finally {
            keliUITimingEvents && keliUITimer(`UI_Browse_loadRangeOfData_rowsgetpage_${queryHash}`, true);
          }

          const loadRows = [];
          if (controller.loadAllRows && rowsPage) {
            pageEnd = Object.keys(rowsPage).length;
            pageEndIndex = pageEnd - 1;
          }
          for (let rowIndexNumber = pageStartIndex; rowIndexNumber <= pageEndIndex; ++rowIndexNumber) {
            const rowId = rowsPage[rowIndexNumber];
            const rowPosition = rowIndexNumber + 1;
            if (!rowId) {
              continue;
            }

            const cachedRow = controller.loadedDataIndex.get(rowId);
            if (cachedRow?.cells) {
              if (rowPosition !== cachedRow.position) {
                cachedRow.position = rowPosition;
                cachedRow.dirty = true;
              }
              if (!controller.cacheNeedsToBeUpdated) {
                continue;
              }
            }
            loadRows.push({
              position: rowPosition,
              rowId
            });
          }

          try {
            keliUITimingEvents && keliUITimer(`UI_Browse_loadRangeOfData_loadAllRows_${queryHash}`);
            if (loadRows?.length) {
              let loadDataForRowPromises = [];
              for (const row of loadRows) {
                if (!controller.cacheNeedsToBeUpdated && controller.loadedDataIndex?.get(row.rowId)?.cells) {
                  continue;
                }
                loadDataForRowPromises.push(controller.loadDataForRow(this.forkThread(), columns, row, rowsObject, queryHash));

                if (loadDataForRowPromises.length === controller.loadRowsBatchSize) {
                  await KeliInterface.nextEventLoop();
                  await Promise.all(loadDataForRowPromises);
                  loadDataForRowPromises = [];
                  await this.checkSuperceded();
                  if (cancelLoading(queryHash)) return;
                }
              }
              await Promise.all(loadDataForRowPromises);
              await this.checkSuperceded();
              if (cancelLoading(queryHash)) return;
            }
          } finally {
            keliUITimingEvents && keliUITimer(`UI_Browse_loadRangeOfData_loadAllRows_${queryHash}`, true);
          }

          await this.checkSuperceded();
          if (cancelLoading(queryHash)) return;
          return {
            finalRenderEnd: pageEnd,
            rowIds: Object.values(rowsPage)
          };
        }
      });
    } catch (error) {
      if (error.superceded) {
        return;
      }
      console.error(error);
      return {error};
    } finally {
      if (needToPopLocalVariable) {
        await this.runKeliFunctionIn(templateInstance, 'popLocalVariableOverride', '_query');
      }
      keliUITimingEvents && keliUITimer('UI_Browse_loadRangeOfData', true);
    }
  }

  async loadDataForRow(templateInstance, columns, row, rowsObject, queryHash) {
    if (!columns || !columns.length) {
      return;
    }
    keliUITimingEvents && keliUITimer(`UI_Browse_loadRangeOfData_loadSingleRow_${row.rowId}`);

    row.background = await this.runKeliFunctionIn(templateInstance, 'getParamFromRow', 'backgroundColor', row.rowId, {p: true});
    row.color = await this.runKeliFunctionIn(templateInstance, 'getParamFromRow', 'color', row.rowId, {p: true});
    row.cells = {};

    const scopeVarOptions = {p: true, confine: true};
    for (const column of columns) {
      const cellName = column.name;
      let isField;
      let isParam;

      if (column._multiSelectColumn) {
        row.cells[cellName] = {
          name: cellName,
          value: await this.runKeliFunctionIn(templateInstance, 'getMultiSelectIdState', row.rowId)
        };
        continue;
      }

      const cell = {
        name: cellName,
        value: await templateInstance.get(`rows.${row.rowId}.${cellName}.displayValue`, {p: true}),
        background: undefined,
        color: undefined
      };

      if (typeof this.backgroundExistsCache[cellName] === 'undefined') {
        isField = rowsObject.hasfield(null, `${cellName}.backgroundColor`);
        if (!isField) {
          isParam = await templateInstance.get(`${cellName}.backgroundColor`, {p: true, decl: true});
        }
        this.backgroundExistsCache[cellName] = !!(isField || isParam);
      }
      if (typeof this.colorExistsCache[cellName] === 'undefined') {
        isField = rowsObject.hasfield(null, `${cellName}.color`);
        if (!isField) {
          isParam = await templateInstance.get(`${cellName}.color`, {p: true, decl: true});
        }
        this.colorExistsCache[cellName] = !!(isField || isParam);
      }

      if (this.backgroundExistsCache[cellName]) {
        cell.background = await templateInstance.get(`rows.${row.rowId}.${cellName}.backgroundcolor`, scopeVarOptions);
      }
      if (this.colorExistsCache[cellName]) {
        cell.color = await templateInstance.get(`rows.${row.rowId}.${cellName}.color`, scopeVarOptions);
      }
      row.cells[cellName] = cell;
    }

    await templateInstance.checkSuperceded();
    if (this.loadedDataIndex && this.loadedDataIndexQueryHash === queryHash) {
      const cachedRow = this.loadedDataIndex.get(row.rowId);
      if (cachedRow) {
        if (!cachedRow.dirty && cachedRow.background !== row.background) {
          cachedRow.dirty = true;
        }
        if (!cachedRow.dirty && cachedRow.color !== row.color) {
          cachedRow.dirty = true;
        }
        if (!cachedRow.dirty && cachedRow?.cells) {
          for (const {name: field, value, background, color} of Object.values(row.cells)) {
            if (cachedRow.cells[field].value !== value ||
              cachedRow.cells[field].background !== background ||
              cachedRow.cells[field].color !== color
            ) {
              cachedRow.dirty = true;
              break;
            }
          }
        }
        Object.assign(cachedRow, row);
      } else {
        this.loadedDataIndex.set(row.rowId, row);
      }
    }
    keliUITimingEvents && keliUITimer(`UI_Browse_loadRangeOfData_loadSingleRow_${row.rowId}`, true);
  }

  renderViewablePage(renderStart, renderEnd) {
    const existingRows = Array.from(this.element.$.body.querySelectorAll('keli-browse-row'));
    let positionsChanged = false;

    keliUITimingEvents && keliUITimer('UI_Browse_renderViewablePage_renderAllRows');
    for (const row of this.loadedDataIndex.values()) {
      if (!row.cells || !Object.keys(row.cells).length) {
        continue;
      }
      if (row.position < renderStart || row.position > renderEnd) {
        continue;
      }
      if (row.rendered && !row.dirty) {
        row.rendered.position = row.position;
        if (row.rendered.isConnected) {
          continue;
        }
      }
      keliUITimingEvents && keliUITimer('UI_Browse_renderViewablePage_renderSingleRow');

      const rowElement = row.rendered || document.createElement('keli-browse-row');
      if (!positionsChanged && rowElement?.parentElement && rowElement.position !== row.position) {
        positionsChanged = true;
      }

      rowElement.rowId = row.rowId;
      rowElement.position = row.position;
      rowElement.color(row.background, row.color);

      for (const column of this.getColumns()) {
        const cell = row.cells?.[column.name];
        if (!cell) {
          continue;
        }
        const cellElement = cell.rendered || rowElement.querySelector(`[data-keliname="${cell.name}"]`) || document.createElement('span');
        cellElement.classList.add('cell');
        if (column._multiSelectColumn) {
          this.configureMultiSelectToggleElement(cellElement, row.rowId, cell.value);
        } else {
          this.formatCellContents(cellElement, cell.value);
        }
        cellElement.dataset.keliname = cell.name;
        rowElement.cellColor(cellElement, cell.background, cell.color);
        if (!cellElement.parentElement) {
          rowElement.appendChild(cellElement);
        }
        cell.rendered = cellElement;
      }

      if (!rowElement.parentElement) {
        // Find the correct position for the element, so they stay in order.
        let closest;
        let closestDistance;
        for (const existingRow of existingRows) {
          if (rowElement === existingRow || existingRow.position < renderStart || existingRow.position > renderEnd) {
            continue;
          }
          if (!closest) {
            closest = existingRow;
            closestDistance = Math.abs(existingRow.position - row.position);
          }
          const distance = Math.abs(existingRow.position - row.position);
          if (distance === 1) {
            closest = existingRow;
            closestDistance = distance;
            break;
          }
          if (distance < closestDistance) {
            closest = existingRow;
            closestDistance = distance;
          }
        }
        if (!existingRows.includes(rowElement)) {
          existingRows.push(rowElement);
        }

        // Insert the new row in the correct DOM position.
        rowElement.browse = this.element;
        if (closest) {
          closest.insertAdjacentElement((closest.position > row.position ? 'beforebegin' : 'afterend'), rowElement);
        } else {
          this.element.$.body.appendChild(rowElement);
        }
      }
      row.rendered = rowElement;
      delete row.dirty;
      keliUITimingEvents && keliUITimer('UI_Browse_renderViewablePage_renderSingleRow', true);
    }

    if (positionsChanged) {
      existingRows
        .sort((a, b) => a.position - b.position)
        .forEach(node => {
          this.element.$.body.appendChild(node);
          node.browse = this.element;
        });
    }

    this.updateAllRowHeights();

    keliUITimingEvents && keliUITimer('UI_Browse_renderViewablePage_renderAllRows', true);
  }

  removeOutOfBoundsRows({
    rendered = true,
    cached = true,
    notRowIds = [],
    notInCache = false,
  } = {}) {
    if (!this.isConnected) return;
    keliUITimingEvents && keliUITimer('UI_Browse_removeOutOfBoundsRows');

    if (cached) {
      for (const [rowId, row] of this.loadedDataIndex) {
        if (notRowIds?.includes(rowId)) {
          continue;
        }
        if (this.element.multiLine) {
          if (this.loadedDataIndex.get(rowId)?.cells) {
            this.loadedDataIndex.set(rowId, {rowId, height: row.height, position: row.position});
          }
        } else {
          this.loadedDataIndex.delete(rowId);
        }
      }
    }

    if (rendered) {
      const rows = this.element.$.body.querySelectorAll('keli-browse-row');
      for (const row of rows) {
        if (notRowIds?.includes(row.rowId)) {
          continue;
        }
        row.remove();
      }
    }

    // As a safety measure, remove any rows from the DOM that do not exist in the cache.
    if (notInCache) {
      const rows = this.element.$.body.querySelectorAll('keli-browse-row');
      const cachedRows = Array.from(this.loadedDataIndex.values()).map(row => row.rendered)
      for (const row of rows) {
        if (cachedRows.includes(row)) {
          continue;
        }
        row.remove();
      }
    }

    keliUITimingEvents && keliUITimer('UI_Browse_removeOutOfBoundsRows', true);
  }

  formatCellContents(cell, displayValue) {
    const iconOrEmojiRegex = /^:(icon|emoji):([a-z0-9_\-]+):$/i;
    if (displayValue && iconOrEmojiRegex.test(displayValue)) {
      const matches = displayValue.match(iconOrEmojiRegex);
      switch (matches[1]) {
        case 'icon':
          cell.innerHTML = `<sl-icon name="${KeliInterface.Styles.parseIconName(matches[2])}"></sl-icon>`;
          break;
        case 'emoji':
          cell.innerHTML = emojify.replace(`:${matches[2]}:`);
          break;
      }
    } else if (displayValue && this.element?.multiLine && /[\r\n]/.test(displayValue.trim())) {
      cell.innerHTML = displayValue.trim().replace(/\n{3,}/g, '\n\n').replace(/\n/g, '<br>');
    } else {
      cell.textContent = displayValue;
    }
  }

  configureMultiSelectToggleElement(cell, rowId, value, indeterminate, slot) {
    if (!cell) {
      return;
    }
    let toggle = cell.querySelector('keli-browse-multiselect-toggle');
    if (!toggle) {
      toggle = document.createElement('keli-browse-multiselect-toggle');
      if (slot) {
        toggle.slot = slot;
      }
      cell.appendChild(toggle);
    }
    toggle.rowId = rowId;
    toggle.indeterminate = indeterminate;
    toggle.selected = value;
  }

  async setEditableCellValue(rowId, element, newValue) {
    await this.runKeliFunction('handleUIChange', {
      rowid: rowId,
      name: element.keliname,
      element: element,
      property: 'displayvalue',
      value: newValue,
      options: {p: true, peek: true}
    });
  }
}

customElements.define(KeliBrowseController.is, KeliBrowseController);
export {KeliBrowseController};
